import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FileUp, CheckCircle2 } from "lucide-react";
import Captcha from "@/components/site/Captcha";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

const initial = {
  candidate_name: "",
  email: "",
  phone: "",
  location: "",
  experience_years: "",
  current_company: "",
  notice_period: "",
  cover_letter: "",
  referrer_name: "",
};

export default function ApplyForm({ jobSlug = null, jobTitle = null, compact = false }) {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);
  const captchaRef = useRef(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach your resume (PDF/DOC/DOCX, up to 5 MB)");
      return;
    }
    const cap = captchaRef.current?.getValues() || {};
    if (!cap.captcha_answer) { toast.error("Please solve the human check"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (jobSlug) fd.append("job_slug", jobSlug);
      fd.append("source", form.referrer_name ? "referral" : "website");
      fd.append("captcha_token", cap.captcha_token || "");
      fd.append("captcha_answer", cap.captcha_answer || "");
      fd.append("resume", file);

      const res = await fetch(`${API_BASE}/applications`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Submission failed");
      toast.success(data.message || "Application submitted");
      setSuccess(true);
      setForm(initial);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err.message || "Submission failed");
      captchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900" data-testid="apply-success">
        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        <h3 className="mt-3 font-heading text-lg font-semibold">Application received</h3>
        <p className="mt-1 text-sm text-emerald-800/90">
          {jobTitle ? `Thanks for applying for ${jobTitle}. ` : "Thanks for sharing your profile. "}
          Our recruiters will review your details and reach out if there's a fit.
        </p>
        <Button variant="outline" className="mt-4 border-emerald-300 bg-white text-emerald-900 hover:bg-emerald-100" onClick={() => setSuccess(false)} data-testid="apply-another">
          Submit another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
      <div className={compact ? "grid gap-4 sm:grid-cols-2" : "grid gap-4"}>
        <div>
          <Label htmlFor="ap-name">Full name *</Label>
          <Input id="ap-name" required value={form.candidate_name} onChange={onChange("candidate_name")} data-testid="apply-name" />
        </div>
        <div>
          <Label htmlFor="ap-email">Email *</Label>
          <Input id="ap-email" type="email" required value={form.email} onChange={onChange("email")} data-testid="apply-email" />
        </div>
        <div>
          <Label htmlFor="ap-phone">Phone *</Label>
          <Input id="ap-phone" required value={form.phone} onChange={onChange("phone")} data-testid="apply-phone" />
        </div>
        <div>
          <Label htmlFor="ap-loc">Current location</Label>
          <Input id="ap-loc" value={form.location} onChange={onChange("location")} data-testid="apply-location" />
        </div>
        <div>
          <Label htmlFor="ap-exp">Experience (years)</Label>
          <Input id="ap-exp" type="number" step="0.1" min="0" value={form.experience_years} onChange={onChange("experience_years")} data-testid="apply-experience" />
        </div>
        <div>
          <Label htmlFor="ap-co">Current company</Label>
          <Input id="ap-co" value={form.current_company} onChange={onChange("current_company")} data-testid="apply-current-company" />
        </div>
        <div className={compact ? "sm:col-span-2" : ""}>
          <Label htmlFor="ap-notice">Notice period</Label>
          <Input id="ap-notice" placeholder="e.g. 60 days" value={form.notice_period} onChange={onChange("notice_period")} data-testid="apply-notice" />
        </div>
      </div>

      <div>
        <Label htmlFor="ap-cover">Why you (optional)</Label>
        <Textarea id="ap-cover" rows={3} value={form.cover_letter} onChange={onChange("cover_letter")} data-testid="apply-cover" />
      </div>

      <div>
        <Label htmlFor="ap-ref">Referred by (optional)</Label>
        <Input id="ap-ref" placeholder="Name of person who referred you" value={form.referrer_name} onChange={onChange("referrer_name")} data-testid="apply-referrer" />
      </div>

      <div>
        <Label htmlFor="ap-file">Resume * (PDF, DOC, DOCX · max 5 MB)</Label>
        <div className="mt-1 flex items-center gap-3">
          <label
            htmlFor="ap-file"
            className="inline-flex cursor-pointer items-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-primary hover:bg-primary/5"
            data-testid="apply-file-label"
          >
            <FileUp className="h-4 w-4" />
            {file ? file.name : "Choose file"}
          </label>
          <input
            id="ap-file"
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="apply-file-input"
          />
        </div>
      </div>

      <Captcha ref={captchaRef} testId="apply-captcha" />

      <Button type="submit" disabled={submitting} className="w-full bg-primary hover:bg-primary/90" data-testid="apply-submit">
        {submitting ? "Submitting…" : "Submit application"}
      </Button>
    </form>
  );
}
